import { useEffect, useState } from "react";
import { useUser } from "../../../contexts/UserContext";
import { ProviderUser } from "../../types/ProviderUser";
import { firebaseDB } from "../../../infrastructure/firebase/firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";

interface UseProviderUsersResult {
  providerUsers: ProviderUser[];
  loading: boolean;
  error: Error | null;
}

export const useProviderUsers = (): UseProviderUsersResult => {
  const { appUser, loading: userLoading } = useUser();
  const [providerUsers, setProviderUsers] = useState<ProviderUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    if (userLoading) {
      setLoading(true);
      return;
    }

    if (appUser) {
      const providerUserIds = Object.values(
        appUser.providerUserIds || {},
      ).flat();

      if (providerUserIds.length > 0) {
        const providerUsersCollection = collection(firebaseDB, "providerUsers");
        const q = query(
          providerUsersCollection,
          where("uid", "in", providerUserIds),
        );

        unsubscribe = onSnapshot(
          q,
          (querySnapshot) => {
            const users = querySnapshot.docs.map(
              (doc) => doc.data() as ProviderUser,
            );
            setProviderUsers(users);
            setLoading(false);
            setError(null);
          },
          (err) => {
            console.error("Error fetching provider users:", err);
            setError(err);
            setLoading(false);
          },
        );
      } else {
        // No provider user IDs available
        setProviderUsers([]);
        setLoading(false);
      }
    } else {
      // User is not authenticated
      setProviderUsers([]);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [appUser, userLoading]);

  return { providerUsers, loading, error };
};
